import { useLocation, useNavigate, useParams } from "react-router";

interface NavigationOptions {
  completed?: boolean;
  resourceCombinationId?: string;
  stopId?: string;
}

type TripsPageParams = {
  resourceCombinationId?: string;
  stopId?: string;
};

const getQueryParam = (options?: NavigationOptions) => {
  return options?.completed !== undefined
    ? `?completed=${options.completed}`
    : "";
};

const TRIPS_PAGE_REGEX = /^\/trips(\?.*)?$/;
const TRIP_STOPS_PAGE_REGEX = /^\/trips\/[^\/]+\/stops(\?.*)?$/;
const STOP_SHIPMENTS_PAGE_REGEX =
  /^\/trips\/[^\/]+\/stops\/[^\/]+\/shipments(\?.*)?$/;

export const useTripsLinks = () => {
  const params = useParams<TripsPageParams>();

  const getTripStopsLink = (options?: NavigationOptions) => {
    const resourceCombinationId =
      options?.resourceCombinationId || params.resourceCombinationId;

    if (resourceCombinationId) {
      return `/trips/${resourceCombinationId}/stops${getQueryParam(options)}`;
    }
    return "#";
  };

  const getStopShipmentsLink = (options?: NavigationOptions) => {
    const resourceCombinationId =
      options?.resourceCombinationId || params.resourceCombinationId;

    const stopId = options?.stopId || params.stopId;

    if (resourceCombinationId && stopId) {
      return `/trips/${resourceCombinationId}/stops/${stopId}/shipments${getQueryParam(
        options
      )}`;
    }
    return "#";
  };

  const getTripsLink = (options?: NavigationOptions) => {
    return `/trips${getQueryParam(options)}`;
  };

  return {
    getTripStopsLink,
    getStopShipmentsLink,
    getTripsLink,
  };
};

export const useTripsNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getTripStopsLink, getStopShipmentsLink, getTripsLink } =
    useTripsLinks();

  const goToTripStops = (options?: NavigationOptions) => {
    const link = getTripStopsLink(options);
    if (link !== "#") {
      navigate(link);
    }
  };

  const goToStopShipments = (options?: NavigationOptions) => {
    const link = getStopShipmentsLink(options);
    if (link !== "#") {
      navigate(link);
    }
  };

  const goToTrips = (options?: NavigationOptions) => {
    navigate(getTripsLink(options));
  };

  const isOnTripsPage = TRIPS_PAGE_REGEX.test(
    location.pathname + location.search
  );
  const isOnTripStopsPage = TRIP_STOPS_PAGE_REGEX.test(
    location.pathname + location.search
  );
  const isOnStopShipmentsPage = STOP_SHIPMENTS_PAGE_REGEX.test(
    location.pathname + location.search
  );

  return {
    goToTripStops,
    goToStopShipments,
    goToTrips,
    isOnTripsPage,
    isOnTripStopsPage,
    isOnStopShipmentsPage,
  };
};
